import { useCallback, useState } from "react";
import _isEmpty from "lodash/isEmpty";
import { REGISTER_TOURNAMENTS } from "../appConstants/APIEndpoint";
import { requestApiPost } from ".";
import { getNXTournamentInfo } from "../pages/TournamentDetails/helper";
import { TournamentDetailsConfig } from "../interfaces/TournamentDetailsConfig";
import { useTokenStore } from "../storage/tokenState";
import { toast } from "react-toastify";
import useTournamentDetails from "./useTournamentDetails";
import { POLLING_INTERVAL_TIME } from "../utils/helpers/constants";
import { checkLocationAndShowLocationModal, setLocationPermissionStatus } from "../store/actions/actions";
import { LOC_REGISTER_TOURNAMENT, LOCATION_PERMISSION_STATUS } from "../components/LocationCheck/helper";
// import { setLocationPermissionStatus } from "../store/actions/locationPermissionStatusActions";
// import { LOCATION_PERMISSION_STATUS } from "../interfaces/LocationPermissionStatus";
import { store } from "../store";
interface IUseRegisterTournaments {
  gameName: string;
  ltid: number;
  type?: string;
}

const useRegisterTournament = ({
  gameName,
  ltid,
  type = "PLAY_WITH_MONEY",
}: IUseRegisterTournaments) => {
  const { fetchTournamentInfo } = useTournamentDetails({ gameName, ltid });
  const { token: userToken } = useTokenStore();
  const { accessToken } = userToken;

  const [loading, setLoading] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [hasGameplayServer, setHasGameplayServer] = useState<boolean>(false);

  /**
   * Poll for tournament information, but stop if gameplayServer is found or after 5 attempts
   */
  const fetchAndPollTournamentInfo = useCallback(() => {
    let count = 0;

    const poll = async () => {
      setIsPolling(true);
      const timestamp = new Date().getTime();
      console.log(`Polling attempt  #${count + 1} Timestamp: ${timestamp}`);
      try {
        await fetchTournamentInfo(false);
        const tournamentInfo = getNXTournamentInfo();
        const gameplayServer = tournamentInfo?.gameplayServer;

        if (!_isEmpty(gameplayServer)) {
          console.log("🎉 Gameplay Server Found! Stopping Polling.");
          setHasGameplayServer(true);
          setIsPolling(false);
          clearInterval(pollingInterval); // Stop polling as gameplayServer is found
        }
      } catch (error) {
        console.warn("Error during polling fetchTournamentInfo", error);
      } finally {
        count++;
        if (count >= 5) {
          console.log("Polling limit reached. Stopping polling.");
          clearInterval(pollingInterval);
          setIsPolling(false);
        }
      }
    };

    poll();
    const pollingInterval = setInterval(poll, POLLING_INTERVAL_TIME);

    return () => clearInterval(pollingInterval); // Cleanup on unmount
  }, [fetchTournamentInfo]);

  const registerTournament = useCallback(
    async (showLoader: boolean) => {
      if (showLoader) {
        setLoading(true);
      }
      const isRGBlocked = false;
      const body = {
        gameName,
        ltid,
        type,
        screenDetails: true,
        isRGBlocked,
      };
      const config = { headers: { Authorization: accessToken } };

      try {
        const res = await requestApiPost<TournamentDetailsConfig>(
          REGISTER_TOURNAMENTS,
          body,
          config
        );
        const response = res?.response;

        if (response?.success) {
          fetchAndPollTournamentInfo();
        } else {
          if (response?.errorType === "location") {
            // First reset location permission status to NOT_ASKED to ensure we get the allow/deny dialog
            store.dispatch(setLocationPermissionStatus(LOCATION_PERMISSION_STATUS.NOT_ASKED));
            await checkLocationAndShowLocationModal({
              ...response,
              action: LOC_REGISTER_TOURNAMENT,
              allowGPS: true // Force showing the allow GPS dialog
            }, () => registerTournament(showLoader));
          } else if (response?.error) {
            // Only show error if there's a specific error message
            console.error('Tournament registration failed:', {
              error: response?.error,
              errorType: response?.errorType,
              response: response
            });
            toast.error(response.error);
          }
        }
      } catch (error: any) {
        toast.error(error?.message);
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [accessToken, fetchAndPollTournamentInfo, gameName, ltid, type]
  );

  return {
    isPolling,
    loading,
    gameName,
    registerTournament,
    hasGameplayServer,
  };
};

export default useRegisterTournament;
